import { createApp } from 'vue';
import App from '@/App.vue';
import pinia from '@/stores';
import router from '@/router';
import api from '@/axios';

import { version } from '../package.json';

// - Sentry
import * as Sentry from '@sentry/vue';
import {
  captureConsoleIntegration,
  extraErrorDataIntegration
} from '@sentry/vue';

// - Quasar
import { Quasar, Notify } from 'quasar';
import quasarLang from 'quasar/lang/zh-TW';
import quasarIconSet from 'quasar/icon-set/svg-material-icons';

// - q-table排序
quasarIconSet.table.arrowUp = 'mdi-arrow-collapse-up';

// - icon集
import '@quasar/extras/material-icons/material-icons.css';
import '@quasar/extras/material-symbols-outlined/material-symbols-outlined.css';
import '@quasar/extras/mdi-v7/mdi-v7.css';

// - sass集
import 'quasar/src/css/index.sass';

// - Calendar
import QCalendar from '@quasar/quasar-ui-qcalendar/src/QCalendarDay.js';
import '@quasar/quasar-ui-qcalendar/src/css/calendar-day.scss';

// - 自定義全域SCSS
import '@/assets/styles/all.scss';

// - 全域 Components
import TitleSeparator from '@/components/TitleSeparator.vue';
import ReportErrorDialog from '@/components/ReportErrorDialog.vue';
import JsonExcel from 'vue-json-excel3';

const app = createApp(App);
app.provide('api', api);

// Initialize Sentry in staging and production environments
if (
  process.env.NODE_ENV === 'staging' ||
  process.env.NODE_ENV === 'production'
) {
  Sentry.init({
    app,
    dsn: 'https://94890ab1f2f5c6e30c6f5c816efe814d@o4506678292840448.ingest.sentry.io/4506684438413312',
    environment: process.env.NODE_ENV,
    release: 'hr-system@' + version,
    normalizeDepth: 5, // 降低深度以減少資料量
    sendDefaultPii: false, // 關閉自動 PII 收集以減少資料量
    maxBreadcrumbs: 50, // 限制 breadcrumb 數量 (預設 100)
    integrations: [
      Sentry.vueIntegration({
        attachProps: false, // 關閉 props 附加以減少資料量
        attachErrorHandler: true,
        logErrors: true
      }),
      captureConsoleIntegration({
        levels: ['error', 'assert'] // 移除 debug 級別，大幅減少事件量
      }),
      extraErrorDataIntegration({
        depth: 5 // 降低深度
      }),
      Sentry.browserTracingIntegration({
        enableInp: false // 關閉 INP 追蹤以減少開銷
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [], // 不記錄網路請求詳情以減少資料量
        networkRequestHeaders: [],
        networkResponseHeaders: []
      })
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      "Failed to execute 'getComputedStyle' on 'Window': parameter 1 is not of type 'Element'.",
      'Error: ResizeObserver loop completed with undelivered notifications.',
      // 過濾廣告攔截器相關錯誤
      'Non-Error promise rejection captured',
      'Script error.',
      // 過濾網路連線問題
      'NetworkError',
      'Network request failed',
      // 過濾常見的第三方錯誤
      'Loading chunk',
      'Loading CSS chunk',
      'ChunkLoadError',
      // 過濾取消的請求
      'AbortError',
      'canceled'
    ],
    denyUrls: [
      // 過濾第三方腳本錯誤
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i
    ],
    // Performance Monitoring - 大幅降低採樣率
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.02 : 0.5, // 生產環境 2%
    // Configure trace propagation for outgoing requests
    tracePropagationTargets: [
      /^\/api/ // 只追蹤 API 請求
    ],
    // Session Replay - 大幅降低採樣率
    replaysSessionSampleRate:
      process.env.NODE_ENV === 'production' ? 0.01 : 0.1, // 生產環境 1%
    replaysOnErrorSampleRate: process.env.NODE_ENV === 'production' ? 0.5 : 1.0, // 錯誤時 50%
    // 在生產環境啟用更嚴格的錯誤過濾
    beforeSend(event) {
      // 過濾非關鍵的 HTTP 錯誤 (如 404)
      if (
        process.env.NODE_ENV === 'production' &&
        event.tags?.httpStatus === 404
      ) {
        return null;
      }
      return event;
    },
    // 過濾不重要的 breadcrumb
    beforeBreadcrumb(breadcrumb) {
      // 過濾 fetch/xhr 的成功請求，只保留錯誤
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        if (breadcrumb.data?.status_code < 400) {
          return null;
        }
      }
      // 過濾 UI 點擊事件 (通常數量很多)
      if (breadcrumb.category === 'ui.click') {
        return null;
      }
      return breadcrumb;
    }
  });
}

app.use(pinia);
app.use(router);
app.use(QCalendar);
app.use(Quasar, {
  plugins: { Notify },
  lang: quasarLang,
  iconSet: quasarIconSet,
  config: {
    brand: {
      primary: '#1976d2',
      secondary: '#26a69a',
      accent: '#9c27b0',
      dark: '#1d1d1d',
      positive: '#21ba45',
      negative: '#c10015',
      info: '#31ccec',
      warning: '#f2c037'
    }
  }
});

// - 自定義Component
app.component('TitleSeparator', TitleSeparator);
app.component('ReportErrorDialog', ReportErrorDialog);
app.component('DownloadExcel', JsonExcel);

app.mount('#app');
